import "./App.css";
import axios from "axios";
import { AuthProvider } from "@/context/AuthContext";
import { Route, Routes, Navigate } from "react-router-dom";

import SignIn from "@/pages/common/signin/SignIn";
// import Unauthorized from "@/pages/common/unauthorized/Unauthorized";

// import HomeAccessControl from "@/components/homeAccesControl.tsx/HomeAccessControl";

import ProtectedLayout from "@/layouts/ProtectedLayout";
import Dashboard from "@/layouts/Dashboard";
import Practitioner from "@/layouts/Practitioner";
import Patient from "@/layouts/Patient";
import Common from "@/layouts/Common";
import PatientsList from "@/pages/practitioner/patientsList/PatientsList";
import ProfilePractitioner from "@/pages/practitioner/profile/ProfilePractitioner";
import NewPatient from "@/pages/practitioner/newPatient/NewPatient";
import PatientFiles from "@/pages/practitioner/patientFiles/PatientFiles";
import PatientCard from "@/pages/practitioner/patientFiles/PatientCard";
import { Programmation } from "@/pages/practitioner/programmation/Programmation";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DashboardAccounts from "@/pages/dashboard/account/DashboardAccounts";
import ProfilePatient from "@/pages/patient/profile/ProfilePatient";
import PatientSessionsList from "@/pages/patient/sessions/PatientSessionsList";
// import { LauncherBilan } from "@/modules/launcher/Launcher";
import CVParams from "@/pages/practitioner/programmation/CVParams";
// import LauncherModules from "@/modules/launcher/LauncherModules";
// import SelectDirectoryImage from "@/components/test/SelectDirectoryImage";
import LauncherModulesPistes from "@/modules/launcher/LauncherModulesPistes";
import { ResultPatient } from "@/pages/practitioner/result/ResultPatient";
import RequestReset from "@/pages/common/signin/RequestReset";
import Reset from "@/pages/common/signin/Reset";
import ModuleLauncherBilan from "@/modules/launcher/LauncherBilan";
import PatientFileLayout from "@/layouts/PatientFileLayout";
import { PatientProvider } from "@/context/PatientContext";
import axiosRetry from "axios-retry";
import * as Sentry from "@sentry/react";
import { AxiosError, AxiosResponse } from "axios";
import { Toaster } from "@/components/ui/toaster";

const BaseUrl = import.meta.env.VITE_API_URL;
axios.defaults.baseURL = BaseUrl;
axios.defaults.withCredentials = true;
axiosRetry(axios, {
  retries: 4,
  // full jitter : entre 0 et base*2^retryCount, plafonné au cap
  retryDelay: (retryCount) => {
    const base = 300; // 300ms
    const cap = 3000; // 3s
    // delay brut
    const expDelay = Math.min(cap, base * Math.pow(2, retryCount));
    // full jitter
    return Math.random() * expDelay;
  },
  // onRetry: (retryCount, error, requestConfig) => {
  //   console.log(
  //     `🔁 Retry #${retryCount} pour ${requestConfig.url} — erreur: ${error.message}`
  //   );
  // },
});

// if (import.meta.env.VITE_API_URL !== "http://localhost:9000") {
//   console.log = function () {};
// }

axios.interceptors.response.use(
  // Pass-through des réponses 2xx
  (response: AxiosResponse) => response,

  // Gestion des erreurs
  (error: AxiosError) => {
    // 1) Breadcrumb pour retrouver l’appel dans Sentry
    Sentry.addBreadcrumb({
      category: "http",
      message: `${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      data: {
        // on stocke status + payload pour debug
        status: error.response?.status,
        payload: error.response?.data,
      },
    });

    // 2) Scope dédié pour enrichir le contexte
    Sentry.withScope((scope) => {
      // balises HTTP
      scope.setTag("http.method", error.config?.method || "UNKNOWN");
      scope.setTag("http.url", error.config?.url || "UNKNOWN");

      if (error.response) {
        // Le serveur a répondu (4xx / 5xx)
        scope.setExtra("http.status_code", error.response.status);
        scope.setExtra("http.response", error.response.data);
      } else if (error.request) {
        // Pas de réponse (timeout / réseau)
        scope.setExtra("http.request", {
          headers: error.config?.headers,
          data: error.config?.data,
        });
        scope.setExtra("http.request_error", error.message);
      } else {
        // Erreur lors de la configuration
        scope.setExtra("http.setup_error", error.message);
      }

      // 3) Capture l’exception une fois dans Sentry
      Sentry.captureException(error);
    });

    // On renvoie toujours la rejection pour pouvoir traiter l’erreur en local
    return Promise.reject(error);
  }
);

function App() {
  return (
    <div>
      <ToastContainer />
      <AuthProvider>
        <PatientProvider>
          <Routes>
            {/* <Route path="/" element={<HomeAccessControl />} /> */}
            <Route element={<Common />}>
              <Route path="/" element={<Navigate to="/connexion" />} />
              <Route path="/connexion" element={<SignIn />} />
              <Route path="/mot-de-passe-oublie" element={<RequestReset />} />
              <Route
                path="/reinitialisation-mot-de-passe/:token"
                element={<Reset />}
              />
              <Route path="/mentions-legales" />
              <Route path="/presentation" />
              <Route path="/contact" />
            </Route>
            <Route element={<ProtectedLayout allowedRoles={["ADMIN"]} />}>
              <Route path="/dashboard" element={<Dashboard />}>
                <Route path="licences" element={<DashboardAccounts />} />
              </Route>
            </Route>
            <Route element={<ProtectedLayout allowedRoles={["PRATICIEN"]} />}>
              <Route path="/praticien" element={<Practitioner />}>
                <Route path="patients" element={<PatientsList />} />
                <Route path=":patientId" element={<PatientFileLayout />}>
                  <Route path="dossier" element={<PatientFiles />} />
                  <Route path="fiche" element={<PatientCard />} />
                  <Route path=":sessionId" element={<Programmation />} />
                  <Route path="resultats" element={<ResultPatient />} />
                  <Route path="programmation" element={<Programmation />} />
                  <Route path="parametragecv" element={<CVParams />} />
                </Route>
                <Route path="nouveau" element={<NewPatient />} />
                <Route path="profil" element={<ProfilePractitioner />} />
                <Route path="modules" />
              </Route>
            </Route>
            <Route element={<ProtectedLayout allowedRoles={["PRATICIEN"]} />}>
              <Route path="/modules">
                <Route path=":patientId">
                  <Route path=":sessionId">
                    <Route
                      path="bilan"
                      element={<ModuleLauncherBilan role="practitioner" />}
                    />
                    {/* <Route path="bilan" element={<LauncherBilan />} /> */}
                    {/* <Route path=":sessionId" element={<SelectDirectoryImage />} /> */}
                    {/* <Route path=":sessionId" element={<LauncherModules />} /> */}
                    <Route
                      path="reeducation"
                      element={<LauncherModulesPistes role="practitioner" />}
                    />
                  </Route>
                </Route>
              </Route>
            </Route>
            <Route element={<ProtectedLayout allowedRoles={["PATIENT"]} />}>
              <Route path="/patient" element={<Patient />}>
                <Route path="seances" element={<PatientSessionsList />} />
                <Route path="contact" element={<ProfilePatient />} />
              </Route>
              <Route path="modules/patient/:sessionId">
                <Route
                  path="bilan"
                  element={<ModuleLauncherBilan role="patient" />}
                />
                <Route
                  path="reeducation"
                  element={<LauncherModulesPistes role="patient" />}
                />
              </Route>
            </Route>
            {/* Redirect to unauthorized page if not authenticated */}
            {/* <Route path="*" element={<Navigate to="/unauthorized" />} />
          <Route path="/unauthorized" element={<Unauthorized />} /> */}
          </Routes>
        </PatientProvider>
      </AuthProvider>
      <Toaster />
    </div>
  );
}

export default App;
